import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import type {
  CompleteDraftOrderRequest,
  DraftOrder,
  PatchAdminV1StoresStoreIdDraftOrdersIdBody,
  PatchAdminV1StoresStoreIdDraftOrdersIdLinesLineIdBody,
  PostAdminV1StoresStoreIdDraftOrdersBody,
  PostAdminV1StoresStoreIdDraftOrdersIdSendInvoiceBody,
} from '@nucleus-commerce/api-client/admin';

import { orderKeys } from '@/features/orders';

import { draftOrdersApi } from '../api/draft-orders-api';

import { draftOrderKeys } from './draft-order-keys';

function remember(queryClient: QueryClient, storeId: string, draft: DraftOrder) {
  queryClient.setQueryData(draftOrderKeys.detail(storeId, draft.id), draft);
  void queryClient.invalidateQueries({
    queryKey: draftOrderKeys.invoicePreviews(storeId, draft.id),
  });
  return queryClient.invalidateQueries({ queryKey: draftOrderKeys.lists(storeId) });
}

export function useCreateDraftOrder(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdDraftOrdersBody) =>
      draftOrdersApi.create(storeId, body),
    meta: { silentError: true },
    onSuccess: (draft) => remember(queryClient, storeId, draft),
  });
}

/**
 * The response is the re-priced draft, so the editor shows the backend's totals and blockers the
 * moment it lands. Errors are rendered by the card that saved.
 */
export function useUpdateDraftOrder(storeId: string, successMessage?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchAdminV1StoresStoreIdDraftOrdersIdBody }) =>
      draftOrdersApi.update(storeId, id, body),
    meta: { ...(successMessage ? { successMessage } : {}), silentError: true },
    onSuccess: (draft) => remember(queryClient, storeId, draft),
  });
}

/** One line in place; the other lines keep their ids. Errors are rendered by the caller. */
export function useUpdateDraftLine(storeId: string, successMessage?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      lineId,
      body,
    }: {
      id: string;
      lineId: string;
      body: PatchAdminV1StoresStoreIdDraftOrdersIdLinesLineIdBody;
    }) => draftOrdersApi.updateLine(storeId, id, lineId, body),
    meta: { ...(successMessage ? { successMessage } : {}), silentError: true },
    onSuccess: (draft) => remember(queryClient, storeId, draft),
  });
}

/** 409 with `meta.blockers` while something is missing; `to` stands in for a missing email. */
export function useSendDraftInvoice(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: PostAdminV1StoresStoreIdDraftOrdersIdSendInvoiceBody;
    }) => draftOrdersApi.sendInvoice(storeId, id, body),
    meta: { successMessage: 'draftOrders:toasts.invoiceSent', silentError: true },
    onSuccess: (draft) => remember(queryClient, storeId, draft),
  });
}

/** The copy is a fresh draft; the caller navigates to it. */
export function useDuplicateDraftOrder(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => draftOrdersApi.duplicate(storeId, id),
    meta: { successMessage: 'draftOrders:toasts.duplicated' },
    onSuccess: (draft) => remember(queryClient, storeId, draft),
  });
}

/** The new order shows up in the orders list; the draft turns read-only and links to it. */
export function useCompleteDraftOrder(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CompleteDraftOrderRequest }) =>
      draftOrdersApi.complete(storeId, id, body),
    meta: { successMessage: 'draftOrders:toasts.completed', silentError: true },
    onSuccess: ({ draftOrder, order }) => {
      queryClient.setQueryData(orderKeys.detail(storeId, order.id), order);
      void queryClient.invalidateQueries({ queryKey: orderKeys.lists(storeId) });
      return remember(queryClient, storeId, draftOrder);
    },
  });
}

export function useCancelDraftOrder(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => draftOrdersApi.cancel(storeId, id),
    meta: { successMessage: 'draftOrders:toasts.cancelled', silentError: true },
    onSuccess: (draft) => remember(queryClient, storeId, draft),
  });
}

/** The detail cache is dropped by the caller once it has navigated away from the editor. */
export function useDeleteDraftOrder(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => draftOrdersApi.remove(storeId, id),
    meta: { successMessage: 'draftOrders:toasts.deleted', silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: draftOrderKeys.lists(storeId) }),
  });
}
